import { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const ORG_CACHE_KEY = 'operota_org';

interface CachedOrg {
  id: string;
  name: string;
  userId?: string;
}

function getCachedOrg(): CachedOrg | null {
  try {
    const raw = localStorage.getItem(ORG_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (parsed?.id && parsed?.name) {
      return {
        id: parsed.id,
        name: parsed.name,
        userId: typeof parsed.userId === 'string' ? parsed.userId : undefined,
      };
    }
  } catch {}

  return null;
}

function setCachedOrg(userId: string, id: string, name: string) {
  try {
    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify({ userId, id, name }));
  } catch {}
}

export function clearCachedOrg() {
  try { localStorage.removeItem(ORG_CACHE_KEY); } catch {}
}

interface OrganizationContextType {
  organizationId: string | null;
  organizationName: string | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const requestIdRef = useRef(0);
  const [initialCachedOrg] = useState<CachedOrg | null>(() => getCachedOrg());
  const [organizationId, setOrganizationId] = useState<string | null>(initialCachedOrg?.id ?? null);
  const [organizationName, setOrganizationName] = useState<string | null>(initialCachedOrg?.name ?? null);
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(initialCachedOrg?.userId ?? null);
  const [isFetching, setIsFetching] = useState(false);

  const applyNoOrgState = useCallback(() => {
    setOrganizationId(null);
    setOrganizationName(null);
    clearCachedOrg();
  }, []);

  const applyOrgState = useCallback((ownerUserId: string, id: string, name: string) => {
    setOrganizationId(id);
    setOrganizationName(name);
    setCachedOrg(ownerUserId, id, name);
  }, []);

  const fetchOrgData = useCallback(async () => {
    if (authLoading) return;

    const requestId = ++requestIdRef.current;

    if (!userId) {
      applyNoOrgState();
      setResolvedUserId(null);
      setIsFetching(false);
      return;
    }

    const cachedOrg = getCachedOrg();
    const hasValidCachedOrg = cachedOrg?.userId === userId;

    if (hasValidCachedOrg) {
      setOrganizationId(cachedOrg.id);
      setOrganizationName(cachedOrg.name);
    } else {
      applyNoOrgState();
    }

    setIsFetching(true);

    try {
      const { data, error } = await supabase.rpc('get_user_org');

      if (requestId !== requestIdRef.current) return;

      if (error) {
        console.error('Failed to fetch organization', error);
        return;
      }

      if (data && data.length > 0) {
        applyOrgState(userId, data[0].id, data[0].name);
      } else {
        applyNoOrgState();
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to fetch organization', error);
    } finally {
      if (requestId === requestIdRef.current) {
        setResolvedUserId(userId);
        setIsFetching(false);
      }
    }
  }, [authLoading, userId, applyNoOrgState, applyOrgState]);

  useEffect(() => {
    void fetchOrgData();
  }, [fetchOrgData]);

  const refetch = useCallback(async () => {
    await fetchOrgData();
  }, [fetchOrgData]);

  const loading = authLoading || (!!userId && resolvedUserId !== userId) || (isFetching && !organizationId);

  return (
    <OrganizationContext.Provider value={{ organizationId, organizationName, loading, refetch }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}
